import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import AutoRenewalConsentSection from "components/AutoRenewalConsentSection";
import Button from "components/Button";
import Container from "components/Container";
import FanRallyTradeMark from "components/FanRallyTradeMark";
import Footer from "components/Footer";
import FullScreenSpinner from "components/FullScreenSpinner";
import Header from "components/Header";
import MarketingConsentSection from "components/MarketingConsentSection";
import Stepper, { StepperContent } from "components/Stepper/Stepper";
import {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import { isDev } from "config/env";
import {
  createAccount,
  createPrimaryUser,
  fetchPlanPackages,
  fetchSignupCharge,
  setSelectedBillingInterval,
  setShouldCharge,
  setStep,
  uploadUserImages,
} from "features/slices/buyFlowSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import useTenantUserFields from "hooks/useTenantUserFields";
import { RegisterScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { useFetchAgreementsQuery } from "queries/agreement.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { trackConversion } from "queries/conversion.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { createContactFetcher } from "queries/user.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Platform, ScrollView, View } from "react-native";
import { useDebouncedCallback } from "use-debounce";
import { getPlanType } from "utils/planPackageUtils";
import toast from "utils/toast";
import TopPortion from "./components/TopPortion";
import {
  calculateSecondardUsersPlanQuantity,
  planPackageIdArray,
} from "./registerUtils";
import StepOne from "./StepOne";
import StepThree from "./StepThree";
import StepTwo from "./StepTwo";
import usePlanType from "./usePlanType";

const getStepName = (step: number) => {
  const stepNames: { [key: number]: string } = {
    1: "Plan & Member Info",
    2: "Additional Members",
    3: "Payment Information",
  };
  return stepNames[step];
};

export const stepText: {
  [keys: number]: string;
} = {
  1: i18n.t("screens.Register.stepOneTitle"),
  2: i18n.t("screens.Register.stepTwoTitle"),
  3: i18n.t("screens.Register.stepThreeTitle"),
};

export default function Register({ navigation, route }: RegisterScreenProps) {
  const { params } = route;
  const dispatch = useAppDispatch();

  const {
    step,
    selectedPlanPackage,
    primaryUser,
    secondaryUsers,
    selectedBillingInterval,
    loading,
    userCreated,
    signupCharge,
    shouldCharge,
  } = useAppSelector((state) => state.buyFlow);
  const { mutateAsync: createContact } = useMutation(createContactFetcher);
  const { mutateAsync: track } = useMutation(trackConversion);

  const sendConversionData = useSendConversionData();
  const planType = usePlanType();
  const furthestStep = useRef(1); // for abandoned cart report

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { data: tenantBranding } = useFetchTenantBrandingQuery();
  const { tenantUserFields } = useTenantUserFields();

  const [emailConsent, setEmailConsent] = useState(
    tenantConfig?.emailMarketingConsentDefault ?? false,
  );
  const [smsConsent, setSmsConsent] = useState(
    tenantConfig?.smsMarketingConsentDefault ?? false,
  );
  const [autoRenewalConsentGiven, setAutoRenewalConsentGiven] = useState(false);
  const { data: autoRenewalAgreements } = useFetchAgreementsQuery(
    "AUTO_RENEWAL",
    selectedPlanPackage?.id,
    selectedBillingInterval,
  );
  const isAutoRenewPlan =
    selectedPlanPackage?.requireAutoRenewalConsent === true;
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlanPackage?.id,
  );

  const quantity =
    primaryUser?.quantity! +
    calculateSecondardUsersPlanQuantity(secondaryUsers);

  const refetchSignupCharge = useDebouncedCallback(
    (selectedPlanPackage) =>
      dispatch(
        fetchSignupCharge({
          planPackageIds: planPackageIdArray(quantity, selectedPlanPackage.id),
          billingInterval: selectedBillingInterval,
        }),
      ),
    250,
  );

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response: StripeTokenResponse | undefined,
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }

    let userId;
    try {
      // meaning user is making a checkout attempt the first time
      if (userCreated === false) {
        // upload user images
        await dispatch(uploadUserImages());
        // create primary user via /user/register endpoint
        const createPrimaryUserResult = await dispatch(
          createPrimaryUser({
            tenantUserFields: tenantUserFields!,
            planPackageUserFields: planPackageUserFields!,
          }),
        ).unwrap();
        userId = createPrimaryUserResult.id;
      }
      // create account
      await dispatch(
        createAccount({
          salesRepId: params?.salesRepId
            ? Number(params.salesRepId)
            : undefined,
          billingToken: response!,
          tenantUserFields: tenantUserFields!,
          planPackageUserFields: planPackageUserFields!,
          emailMarketingConsent: emailConsent,
          smsMarketingConsent: smsConsent,
          autoRenewalAgreementId:
            isAutoRenewPlan && autoRenewalConsentGiven
              ? autoRenewalAgreements?.[0]?.id
              : undefined,
        }),
      ).unwrap();

      await track({
        step: "PURCHASE_COMPLETE",
        flow: "REGISTER",
        userId: userId || primaryUser.id, // if userId is undefined, then the user must has been created
      });

      sendConversionData(signupCharge, quantity, response!);

      // Force navigate to home screen because if user registers through the shop page, they will be redirected back to the shop page
      // @ts-ignore
      navigation.push("AuthenticatedFlow");
    } catch (rejectedValueOrSerializedError) {
      console.log(rejectedValueOrSerializedError);
    }
  };

  const stepNames = useMemo(() => {
    const disableAllAdditionalMembers =
      selectedPlanPackage?.disableAddAdultInBuyFlow &&
      selectedPlanPackage?.disableAddChildInBuyFlow;

    const steps = {
      personal: {
        name: "Personal Info",
        icon: (
          <MaterialCommunityIcons name="account-edit" size={16} color="white" />
        ),
      },
      members: {
        name: disableAllAdditionalMembers ? "" : "Members",
        icon: (
          <MaterialCommunityIcons
            name="account-group"
            size={16}
            color="white"
          />
        ),
      },
      payment: {
        name: "Payment",
        icon: (
          <MaterialCommunityIcons name="credit-card" size={16} color="white" />
        ),
      },
    };

    if (shouldCharge) {
      // For bank plans, do not include additional members step
      if (planType === "BANK") {
        return [steps.personal, steps.payment];
      } else {
        return [steps.personal, steps.members, steps.payment];
      }
    } else {
      // For bank plans, do not include additional members step
      if (planType === "BANK") {
        return [steps.personal];
      } else {
        return [steps.personal, steps.members];
      }
    }
  }, [planType, shouldCharge, selectedPlanPackage]);

  const determinePreviousStep = () => {
    if (!selectedPlanPackage) return;
    let previousStep;
    if (getPlanType(selectedPlanPackage) === "BANK") {
      previousStep = 1;
    } else {
      previousStep = step - 1;
    }
    dispatch(setStep(previousStep));
  };

  useEffect(() => {
    if (tenantConfig?.disableBuyflow) {
      navigation.replace("Login");
      return;
    }
  }, [tenantConfig]);

  useEffect(() => {
    const planPackageId = params ? params.preSelectedPlanPackageId : null;
    dispatch(fetchPlanPackages(planPackageId));
  }, []);

  // refetch signup charge when selected plan package or billing interval change
  useEffect(() => {
    if (!selectedPlanPackage) return;
    if (parseFloat(selectedPlanPackage.fullPrice) === 0) {
      dispatch(setShouldCharge(false));
    } else {
      dispatch(setShouldCharge(true));
    }
    dispatch(
      setSelectedBillingInterval(selectedPlanPackage.billingIntervals[0]),
    );
    refetchSignupCharge(selectedPlanPackage);
    setAutoRenewalConsentGiven(false);
  }, [selectedPlanPackage]);

  useEffect(() => {
    if (!selectedPlanPackage) return;
    refetchSignupCharge(selectedPlanPackage);
  }, [selectedBillingInterval, quantity]);

  useEffect(() => {
    if (Platform.OS !== "web" || isDev) return;

    // update furthest step
    if (step > furthestStep.current) {
      furthestStep.current = step;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") return;
      const emailInput = document.querySelector(
        'input[data-testid="@email/input"] ',
      ) as HTMLInputElement;
      const firstNameInput = document.querySelector(
        'input[data-testid="@firstname/input"]',
      ) as HTMLInputElement;
      const lastNameInput = document.querySelector(
        'input[data-testid="@lastname/input"]',
      ) as HTMLInputElement;
      const phoneInput = document.querySelector(
        'input[data-testid="@phone/input"]',
      ) as HTMLInputElement;

      if (emailInput?.value) {
        createContact({
          firstName: firstNameInput.value,
          lastName: lastNameInput.value,
          phone: phoneInput.value,
          email: emailInput.value,
          source: "REGISTRATION",
          planPackageId: selectedPlanPackage?.id,
          buyflowStep: getStepName(furthestStep.current),
        });
      }
    };
    const title = `Register - step ${step}`;
    document.title = title;
    ReactGA.send({ hitType: "pageview", page: title });

    // https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [step, selectedPlanPackage]);

  return (
    <ScrollView contentContainerStyle={{ flex: 1 }}>
      <Header programName={selectedPlanPackage?.name} />
      {loading && (
        <FullScreenSpinner
          style={{ padding: 15 }}
          text={i18n.t("screens.Register.creatingAccountMessage")}
        />
      )}
      <Container className="max-w-3xl pt-4 flex-grow">
        <Stepper step={step} steps={stepNames}>
          <StepperContent>
            <StepOne
              createAccount={() => handleTokenReceived(undefined, undefined)}
            />
          </StepperContent>
          <StepperContent>
            {planType !== "BANK" && (
              <StepTwo
                selectedPlanPackage={selectedPlanPackage}
                selectedBillingInterval={selectedBillingInterval}
                onContinuePress={() =>
                  handleTokenReceived(undefined, undefined)
                }
              />
            )}
          </StepperContent>
          <StepperContent>
            {shouldCharge && selectedPlanPackage && (
              <StepThree
                selectedPlanPackage={selectedPlanPackage}
                selectedBillingInterval={selectedBillingInterval}
                onTokenReceived={handleTokenReceived}
                disabled={
                  !!autoRenewalAgreements?.length && !autoRenewalConsentGiven
                }
                beforeButton={
                  <AutoRenewalConsentSection
                    agreements={autoRenewalAgreements}
                    consentGiven={autoRenewalConsentGiven}
                    onChange={setAutoRenewalConsentGiven}
                  />
                }
              >
                <MarketingConsentSection
                  style={{ marginBottom: 24 }}
                  tenantName={tenantBranding?.name}
                  emailConsent={emailConsent}
                  smsConsent={smsConsent}
                  onEmailChange={setEmailConsent}
                  onSmsChange={setSmsConsent}
                />
              </StepThree>
            )}
          </StepperContent>
        </Stepper>
        {step > 1 && (
          <View className="self-start mt-8">
            <Button
              onPress={determinePreviousStep}
              testID="register-back-button"
            >
              Previous
            </Button>
          </View>
        )}
        <View className="justify-center items-center mb-8">
          <TopPortion />
          <FanRallyTradeMark />
        </View>
      </Container>
      <Footer />
    </ScrollView>
  );
}
